import React, { useState, useEffect, useRef } from 'react';
import { BOOKING_URL } from '../config';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ScribbleUnderline from './ScribbleUnderline';

const BASE_TIERS = [
  {
    key: 'personal',
    name: 'Personal',
    price: 499,
    tagline: 'For individuals who want their time back.',
    highlighted: false,
    features: [
      'Cloud-hosted, dedicated to you',
      'Calendar & scheduling management',
      'Message drafting & inbox triage',
      'Reminders & life admin',
      'Works via WhatsApp, email & more',
      '3 integrations included',
      '30-day post-setup support',
      'Done-with-you onboarding',
    ],
  },
  {
    key: 'personal-plus',
    name: 'Personal Plus',
    price: 899,
    tagline: 'For power users who want Claw fully tuned to their life.',
    highlighted: true,
    features: [
      'Everything in Personal, plus:',
      'Unlimited integrations',
      'Travel research & itinerary building',
      'Advanced tone & voice training',
      'Weekly summary reports',
      'Priority setup & onboarding',
      'Extended 60-day support',
    ],
  },
];

const SUPPORT_FEATURES = [
  { text: 'Hands-on retraining as your life changes' },
  { text: 'Direct access to your setup specialist' },
  { text: 'Priority response within 4 hours' },
  { text: 'New integrations added on request' },
  { text: 'Monthly performance reviews' },
];

const SupportDropdown = ({ highlighted }) => (
  <motion.div
    initial={{ opacity: 0, y: -6, scale: 0.97 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -4, scale: 0.97 }}
    transition={{ duration: 0.18 }}
    className={`absolute z-20 left-0 right-0 mt-2 rounded-xl border shadow-lg overflow-hidden ${
      highlighted ? 'bg-[#1E1E1C] border-[#3A3A36]' : 'bg-white border-[#E8E2DA]'
    }`}
  >
    <div className={`px-4 py-3 border-b ${highlighted ? 'border-[#2A2A26]' : 'border-[#F0ECE8]'}`}>
      <p className={`text-[11px] font-bold uppercase tracking-wider font-mono-custom ${highlighted ? 'text-[#7AAAC8]' : 'text-[#2D6A8F]'}`}>
        What's included with support
      </p>
    </div>
    <ul className="px-4 py-3 space-y-2.5">
      {SUPPORT_FEATURES.map((f, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <SafeIcon icon={FiIcons.FiCheck} className={`text-xs flex-shrink-0 mt-0.5 ${highlighted ? 'text-[#7AAAC8]' : 'text-[#2D6A8F]'}`} />
          <span className={`text-[12px] leading-snug ${highlighted ? 'text-[#C8C0B4]' : 'text-[#6B6560]'}`}>{f.text}</span>
        </li>
      ))}
    </ul>
    <div className={`px-4 py-2.5 border-t ${highlighted ? 'border-[#2A2A26] bg-[#161614]' : 'border-[#F0ECE8] bg-[#F8F6F3]'}`}>
      <p className="text-[10px] font-mono-custom text-[#9E9690]">Cancel anytime · billed monthly after setup</p>
    </div>
  </motion.div>
);

const SupportToggle = ({ tier, supportAdded, onToggleSupport }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={`mb-5 p-3.5 rounded-xl border relative ${
        tier.highlighted
          ? supportAdded ? 'bg-[#2D6A8F]/20 border-[#2D6A8F]' : 'bg-[#2A2A26] border-[#3A3A36]'
          : supportAdded ? 'bg-[#EBF5F0] border-[#8ACAAA]' : 'bg-[#F8F6F3] border-[#E8E2DA]'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-[12px] font-semibold leading-tight ${tier.highlighted ? 'text-[#F8F6F3]' : 'text-[#1A1A18]'}`}>
            Add ongoing support
          </p>
          <p className={`text-[10px] mt-0.5 ${tier.highlighted ? 'text-[#9E9690]' : 'text-[#9E9690]'}`}>
            <strong className={tier.highlighted ? 'text-[#7AAAC8]' : 'text-[#2D6A8F]'}>+£99/mo</strong>
            {' · '}
            <button
              onClick={() => setOpen((o) => !o)}
              className={`underline underline-offset-2 transition-colors ${
                tier.highlighted ? 'text-[#9E9690] hover:text-[#C8C0B4]' : 'text-[#9E9690] hover:text-[#6B6560]'
              }`}
            >
              do I need support?
              <SafeIcon icon={open ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="inline ml-0.5 text-[9px] relative -top-px" />
            </button>
          </p>
        </div>
        <button
          onClick={onToggleSupport}
          className={`relative w-11 h-6 rounded-full transition-all duration-300 flex-shrink-0 ${
            supportAdded ? 'bg-[#3D7A5E]' : tier.highlighted ? 'bg-[#3A3A36]' : 'bg-[#D4CEC8]'
          }`}
        >
          <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-300 ${supportAdded ? 'left-5' : 'left-0.5'}`} />
        </button>
      </div>
      <AnimatePresence>{open && <SupportDropdown highlighted={tier.highlighted} />}</AnimatePresence>
    </div>
  );
};

const PricingTier = ({ tier, index, isRecommended, supportAdded, onToggleSupport }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className={`relative rounded-2xl flex flex-col ${tier.highlighted ? 'lg:-mt-4 lg:mb-4' : ''}`}
  >
    {tier.highlighted && (
      <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2D6A8F] to-transparent" />
    )}
    {isRecommended && (
      <motion.div
        className="absolute -top-3 left-1/2 -translate-x-1/2 z-10"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
      >
        <div className="flex items-center gap-1.5 px-3 py-1 bg-[#C17B4A] rounded-full text-white text-[10px] font-bold shadow-md whitespace-nowrap">
          <SafeIcon icon={FiIcons.FiStar} className="text-[10px]" />
          Recommended for you
        </div>
      </motion.div>
    )}
    <div
      className={`relative p-8 rounded-2xl flex flex-col flex-grow h-full transition-all duration-300 ${
        tier.highlighted
          ? 'bg-[#1A1A18] text-[#F8F6F3] border border-[#2A2A26]'
          : 'bg-white border border-[#E8E2DA]'
      } ${isRecommended ? 'ring-2 ring-[#C17B4A] ring-offset-2 shadow-xl' : ''}`}
    >
      {tier.highlighted && (
        <div className="absolute top-4 right-4 px-2.5 py-1 bg-[#2D6A8F] rounded-full text-[9px] font-mono-custom font-semibold uppercase tracking-wider text-white">
          Most popular
        </div>
      )}
      <div className="mb-7">
        <p className={`font-mono-custom text-[9px] tracking-[0.25em] uppercase font-semibold mb-3 ${tier.highlighted ? 'text-[#7AAAC8]' : 'text-[#2D6A8F]'}`}>
          {tier.name}
        </p>
        <div className="flex items-baseline gap-2 mb-2">
          <span className={`text-4xl font-bold tracking-tight ${tier.highlighted ? 'text-[#F8F6F3]' : 'text-[#1A1A18]'}`}>
            £{tier.price.toLocaleString()}
          </span>
          <span className="text-sm text-[#9E9690]">one-time</span>
        </div>
        <p className={`text-sm leading-relaxed ${tier.highlighted ? 'text-[#9E9690]' : 'text-[#6B6560]'}`}>
          {tier.tagline}
        </p>
      </div>
      <ul className="space-y-3.5 mb-6 flex-grow">
        {tier.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3">
            <div className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${tier.highlighted ? 'bg-[#2D6A8F]' : 'bg-[#EBF2F9]'}`}>
              <SafeIcon icon={FiIcons.FiCheck} className={`text-[9px] ${tier.highlighted ? 'text-white' : 'text-[#2D6A8F]'}`} />
            </div>
            <span className={`text-sm leading-relaxed ${tier.highlighted ? 'text-[#C8C0B4]' : 'text-[#6B6560]'}`}>{f}</span>
          </li>
        ))}
      </ul>
      <SupportToggle tier={tier} supportAdded={supportAdded} onToggleSupport={onToggleSupport} />
      {tier.highlighted ? (
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-white text-[#1A1A18] hover:bg-[#F0ECE8]"
        >
          <SafeIcon icon={FiIcons.FiCalendar} className="text-xs" />
          Book a Demo
        </a>
      ) : (
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-[#1A1A18] text-[#F8F6F3] hover:bg-[#2A2A26]"
        >
          <SafeIcon icon={FiIcons.FiSearch} className="text-xs" />
          See if it's a fit
        </a>
      )}
      <p className={`text-center text-[10px] mt-2.5 ${tier.highlighted ? 'text-[#5A5A56]' : 'text-[#B0A89E]'}`}>
        20-min call · no commitment required
      </p>
    </div>
  </motion.div>
);

const SupportAddon = () => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: 0.15 }}
    className="mt-5 max-w-3xl mx-auto"
  >
    <div className="bg-[#F8F6F3] border border-[#E8E2DA] rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
      <div className="flex-grow">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <p className="text-[14px] font-bold text-[#1A1A18]">Ongoing Support</p>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EBF2F9] border border-[#C8DDE9] text-[#2D6A8F] font-mono-custom uppercase tracking-wider">
            Add-on
          </span>
        </div>
        <p className="text-[13px] text-[#6B6560] leading-relaxed mb-3">
          Keep Claw sharp as your life changes — retraining, new integrations, priority support and monthly reviews. Available with either plan.
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {SUPPORT_FEATURES.map((f, i) => (
            <li key={i} className="flex items-center gap-2 text-[12px] text-[#6B6560]">
              <SafeIcon icon={FiIcons.FiCheck} className="text-[#2D6A8F] text-xs flex-shrink-0" />
              {f.text}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex-shrink-0 flex flex-col items-end gap-3">
        <div className="text-right">
          <p className="text-[22px] font-bold text-[#1A1A18] tracking-tight">
            £99<span className="text-sm font-normal text-[#9E9690]">/mo</span>
          </p>
          <p className="text-[10px] text-[#9E9690] font-mono-custom mt-0.5">cancel anytime</p>
        </div>
        
      </div>
    </div>
  </motion.div>
);

const Pricing = () => {
  const [recommendedKey, setRecommendedKey] = useState(null);
  const [supportAdded, setSupportAdded] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem('clawRecommendedPlan');
    if (stored) {
      setRecommendedKey(stored);
      if (stored === 'personal-support' || stored === 'personal-plus-support') setSupportAdded(true);
    }
    const handler = () => {
      const updated = sessionStorage.getItem('clawRecommendedPlan');
      if (updated) {
        setRecommendedKey(updated);
        if (updated === 'personal-support' || updated === 'personal-plus-support') setSupportAdded(true);
      } else {
        setRecommendedKey(null);
        setSupportAdded(false);
      }
    };
    window.addEventListener('clawPlanUpdated', handler);
    return () => window.removeEventListener('clawPlanUpdated', handler);
  }, []);

  const isRecommended = (tierKey) => {
    if (!recommendedKey) return false;
    if (recommendedKey === tierKey) return true;
    if (recommendedKey === 'personal-support' && tierKey === 'personal') return true;
    if (recommendedKey === 'personal-plus-support' && tierKey === 'personal-plus') return true;
    return false;
  };

  return (
    <section id="pricing" className="py-32 bg-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <p className="font-handwritten text-2xl text-[#C17B4A]" style={{ transform: 'rotate(-0.5deg)', display: 'inline-block' }}>
            "Like having a brilliant friend who's always one message away."
          </p>
        </motion.div>
        <div className="text-center mb-14">
          <div className="tag-pill mx-auto mb-5 w-fit">Simple pricing</div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-[#1A1A18] mb-3 tracking-tight leading-tight" style={{ letterSpacing: '-0.02em' }}>
            One fee. No subscription.<br />
            <span className="gradient-text-accent">Claw is yours.</span>
          </h2>
          <ScribbleUnderline color="rgba(45,106,143,0.25)" width={220} className="mx-auto mb-5" />
          <p className="text-lg text-[#6B6560] max-w-xl mx-auto">
            Pay once to set Claw up. We handle everything on a free call — then Claw is live within 24 hours.
          </p>
          {recommendedKey && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-[#FBF4E8] border border-[#DFC98A] rounded-full text-[#8A6A2A] text-[12px] font-semibold"
            >
              <SafeIcon icon={FiIcons.FiStar} className="text-[12px]" />
              Your quiz result is highlighted below
            </motion.div>
          )}
        </div>
        <div className="grid lg:grid-cols-2 gap-5 max-w-3xl mx-auto pt-4">
          {BASE_TIERS.map((tier, i) => (
            <PricingTier
              key={tier.key}
              tier={tier}
              index={i}
              isRecommended={isRecommended(tier.key)}
              supportAdded={supportAdded}
              onToggleSupport={() => setSupportAdded((p) => !p)}
            />
          ))}
        </div>
        <SupportAddon />
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-10 mb-6"
        >
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#F8F6F3] border border-[#E8E2DA]">
            <SafeIcon icon={FiIcons.FiShield} className="text-[#2D6A8F] flex-shrink-0" />
            <span className="text-sm text-[#6B6560]">
              <strong className="text-[#1A1A18]">30-day satisfaction guarantee.</strong>{' '}
              If Claw isn't saving you time by day 30, we'll retrain or refund in full.
            </span>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-8"
        >
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="btn-primary text-[16px] px-10 py-4 flex items-center gap-2 mx-auto shadow-lg">
            <SafeIcon icon={FiIcons.FiCalendar} />
            Book a free setup call
          </a>
          <p className="text-[12px] text-[#9E9690] mt-3">
            20-min call · we'll recommend the right plan · live within 24h
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;